import { useRef, useState, useEffect, useCallback } from 'react'
import throttle from 'lodash.throttle'
import * as R from 'ramda'

import {
  conversationsFragment,
  messagesFragment,
  sharedConversationFragment
} from 'src/lib/data/graphFragments'

import transact from 'src/lib/data/transact'
import { socketSend, socketOn, socketOff } from 'src/lib/socket'

import { baseUserAttrs } from 'src/lib/data/graphFragments'
import { fetchGraph2 } from 'src/lib/data/fetch-graph'

export const findSharedThreads = (userId, friends) => async (dispatch, _) => {
  const graphQuery = sharedConversationFragment(friends)
  const res = await dispatch(fetchGraph2(null, graphQuery, userId))
  const sharedThreads = R.path(
    ['@loggedInUser', '@user/shared-message-threads'],
    res
  )

  return sharedThreads
}

// TODO pass in key selection from above
export const fetchThreads = userId => async (dispatch, _) => {
  // TODO
  // sort and paginate here
  const graphQuery = conversationsFragment()
  const res = await dispatch(fetchGraph2(null, graphQuery, userId))

  return res
}

export const fetchMessages = threadId => async (dispatch, _) => {
  // TODO
  // sort and paginate here
  const graphQuery = messagesFragment()
  const res = await dispatch(fetchGraph2(null, graphQuery, threadId))
  const messages = R.prop('@conversations/thread-messages', res)

  return R.sortBy(R.prop(':system/created-at'), messages)
}

const TYPING_THROTTLE = 1000
export const TYPING_TIMEOUT = TYPING_THROTTLE * 2

export const sendMessage = async (
  user,
  threadId,
  message,
  updateConversations,
  network
) => {
  const res = await transact([
    [
      '@user/add-thread-message',
      { threadId, message, networkId: R.prop(':db/id', network) }
    ]
  ])

  const reifiedMessageData = {
    ':db/id': res['temp-message'],
    ':thread-message/body': message,
    ':thread-message/author': R.pick(baseUserAttrs, user),
    ':system/created-at': Date.now()
  }

  updateConversations(state => {
    return {
      ...state,
      messages: [...(state.messages || []), reifiedMessageData]
    }
  })

  socketSend({
    action: 'broadcast',
    data: {
      roomId: threadId,
      payload: { action: 'new-message', data: reifiedMessageData }
    }
  })
}

export const sendTyping = throttle((userId, threadId) => {
  socketSend({
    action: 'broadcast',
    data: {
      roomId: threadId,
      payload: { action: 'typing-message', data: userId }
    }
  })
}, TYPING_THROTTLE)

export const useJoinThread = (threadId, userId) =>
  useEffect(() => {
    if (threadId) {
      socketSend({
        action: 'join',
        data: { roomId: threadId, userId }
      })
    }

    return () => {
      if (threadId) {
        socketSend({
          action: 'leave',
          data: { roomIds: [threadId] }
        })
      }
    }
  }, [threadId])

export const useObserveEvent = (event, callback) =>
  useEffect(() => {
    socketOn(event, callback)

    return () => {
      socketOff(event, callback)
    }
  }, [event, callback])

export const useObserveTyping = thread => {
  const timeoutRef = useRef()
  const [typing, setTyping] = useState({})

  const onTyping = useCallback(
    userId => {
      setTyping(R.assoc(userId, true))

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setTyping(R.assoc(userId, false))
        timeoutRef.current = null
      }, TYPING_TIMEOUT)
    },
    [setTyping]
  )

  useObserveEvent('typing-message', onTyping)

  const members = R.propOr([], ':thread/members', thread)
  const filteredPairs = R.filter(([_, val]) => !!val, R.toPairs(typing) || [])

  return R.map(
    id => {
      return R.find(R.propEq(':user/squuid', id), members)
    },
    R.map(R.head, filteredPairs) || []
  )
}

export const useObserveMessages = cb => useObserveEvent('new-message', cb)
